"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import EnqueryForm from "@/components/forms/EnqueryForm";
import { SuccessMessageType } from "@/interface/types";
import useModalScrollLock from "@/hooks/useModalScrollLock";
import { downloadRoadmapPdf } from "@/lib/brochures";

interface BrochurePopupFormProps {
  /** "CPA" | "CMA" | "EA" — picks both the heading and the PDF. */
  pageName: string;
  handleClose: () => void;
}

const courseIds: Record<string, number> = { CPA: 1, CMA: 2, EA: 19 };

const CloseGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    className="h-5 w-5"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/**
 * The brochure-download popup. Deliberately separate from PopupForm: that one
 * is the webinar modal (white card, banner artwork, "Register For Free
 * Webinar") and is used across many pages, so it can't be restyled for this.
 */
const BrochurePopupForm: React.FC<BrochurePopupFormProps> = ({
  pageName,
  handleClose,
}) => {
  const [mounted, setMounted] = useState(false);

  useModalScrollLock(true, {
    restoreScrollPosition: true,
    preventTouchScroll: true,
  });

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleClose]);

  /*
   * Fires the download the moment the form reports success.
   *
   * Both the ref and the useCallback are load-bearing. EnqueryForm calls this
   * from an effect keyed on the callback's identity, so an inline arrow would
   * be a new function every render and re-run the effect — downloading the PDF
   * over and over. useCallback pins the identity; the ref makes it idempotent
   * even if it is called again.
   */
  const hasDownloaded = useRef(false);
  // Drives the heading swap below: the brochure title is a prompt to fill the
  // form, so it has no business sitting above the thank-you.
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSuccessStateChange = useCallback(
    (isSuccess: boolean) => {
      setIsSubmitted(isSuccess);
      if (!isSuccess || hasDownloaded.current) return;
      hasDownloaded.current = true;
      void downloadRoadmapPdf(pageName);
    },
    [pageName],
  );

  if (!mounted) return null;

  // Portalled to <body> so the overlay can't be trapped by an ancestor
  // stacking context.
  return createPortal(
    <div
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Download the U.S. ${pageName} brochure`}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-4"
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="relative max-h-[90vh] w-full max-w-[420px] overflow-y-auto rounded-2xl bg-[#0D0D0D] p-6"
        style={{ boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)" }}
        data-modal-scrollable
      >
        {/* no-min-size opts out of globals.css's
            `button:not(.no-min-size) { min-width: 32px }`, which would stretch
            this square hit area into a rectangle. */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="no-min-size absolute right-4 top-4 z-10 flex h-6 w-6 min-w-[24px] items-center justify-center text-white"
        >
          <CloseGlyph />
        </button>

        {/* Rendered here rather than through EnqueryForm's formHeading, so the
            shared form needs no new case for a heading only this popup uses.
            Dropped once the form succeeds — the success state below carries its
            own "Thank you!" heading, and stacking the two read as a leftover. */}
        {!isSubmitted && (
          <h2 className="mb-5 text-center text-2xl font-bold leading-tight text-white">
            Download AI-integrated
            <span className="mt-1 block text-[#FFBA08]">
              U.S. {pageName} Brochure
            </span>
          </h2>
        )}

        {/* isAStepperForm is what skips the form for anyone who has already
            given us their details. EnqueryForm reads them back from
            localStorage and, with this flag on, jumps straight to the success
            step on mount. That fires onSuccessStateChange above, so the
            brochure starts downloading immediately and the visitor sees the
            thank-you instead of a form they've already filled in once.

            Worth knowing what "already filled a form" means here: the key is
            MILES_NEXT_F1_REF_ENQUIRY, which only EnqueryForm writes. GlobalForm
            keeps its own (MILES_NEXT_F1_REF), so someone whose last submission
            was through a GlobalForm page still sees the form here.

            A first-time visitor has nothing stored, so the flag does nothing
            and they get the normal form.

            gtmSubmitEvent keeps the visitor on the page. The default
            "gtm_form1_filled" is watched by a tag in GTM that redirects to
            cpa-course.mileseducation.com/thank-you — which would navigate away
            mid-download and replace the message below. */}
        <EnqueryForm
          courseId={courseIds[pageName] ?? 1}
          pageName={pageName}
          darkBackground
          isAStepperForm
          formType="page"
          submitButtonName="Get The Brochure"
          successMessage={SuccessMessageType.ACCOUNTING}
          gtmSubmitEvent="gtm_form1_filled"
          onSuccessStateChange={handleSuccessStateChange}
        />
      </div>
    </div>,
    document.body,
  );
};

export default BrochurePopupForm;
